"use client";

import { useState } from "react";
import type {
  CapacityOverride,
  OpeningHour,
} from "@/modules/scheduling/surfaces";
import { useFormAction, type FormAction } from "./useFormAction";
import { buttonClass } from "@/design/buttons";
import { Banner } from "./ui";

/**
 * How many appointments this person holds per hour, at this clinic (H41,
 * docs/ux/screens/PractitionerCalendarScreen.md).
 *
 * **A weekly grid, not a control on the day cards above it.** The number is
 * keyed by (weekday, hour), so putting it on a dated card would read as "raise
 * it for that Tuesday" and be wrong every following Tuesday. Only the hours the
 * clinic is actually open appear: capacity for an hour nobody can book is a
 * number with nothing behind it.
 *
 * 1 is the default and stores nothing, which is why an ordinary clinic never
 * touches this screen and has no rows in the table.
 */

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CapacityGrid({
  practitionerId,
  clinicHours,
  overrides,
  save,
}: {
  practitionerId: string;
  clinicHours: OpeningHour[];
  overrides: CapacityOverride[];
  save: FormAction;
}) {
  const { state, pending, onSubmit } = useFormAction(save);
  const saved = new Map(
    overrides.map((o) => [`${o.weekday}:${o.hour}`, o.capacity]),
  );
  const [values, setValues] = useState<Map<string, number>>(
    () => new Map(saved),
  );

  const hours = [...new Set(clinicHours.map((h) => h.hour))].sort((a, b) => a - b);
  const openOn = new Set(clinicHours.map((h) => `${h.weekday}:${h.hour}`));

  if (hours.length === 0) return null;

  const capacityAt = (weekday: number, hour: number) =>
    values.get(`${weekday}:${hour}`) ?? 1;

  return (
    <section className="mt-6 rounded-xl border border-line bg-surface p-5 shadow-sm">
      <h2 className="mb-1 font-bold text-ink">How many per hour</h2>
      <p className="mb-4 text-sm text-muted">
        One unless you raise it. Customers never see these numbers; they see a
        time as available or already booked.
      </p>

      {state.formError && <Banner tone="danger">{state.formError}</Banner>}
      {state.success && <Banner tone="success">Saved.</Banner>}

      <form onSubmit={onSubmit}>
        <input type="hidden" name="practitionerId" value={practitionerId} />
        <div className="overflow-x-auto">
          <table className="border-separate border-spacing-1 text-center text-sm">
            <thead>
              <tr>
                <th className="w-14" />
                {DAYS.map((day) => (
                  <th key={day} className="text-xs font-bold text-muted">
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map((hour) => (
                <tr key={hour}>
                  <th className="pe-2 text-end font-mono text-xs font-medium text-muted">
                    {String(hour).padStart(2, "0")}:00
                  </th>
                  {DAYS.map((day, weekday) => {
                    const isOpen = openOn.has(`${weekday}:${hour}`);
                    if (!isOpen) {
                      return (
                        <td key={day} className="p-0">
                          <div className="h-9 w-14 rounded-md border border-dashed border-line" />
                        </td>
                      );
                    }
                    return (
                      <td key={day} className="p-0">
                        <input
                          type="number"
                          min={1}
                          max={99}
                          aria-label={`${day} ${String(hour).padStart(2, "0")}:00 capacity`}
                          value={capacityAt(weekday, hour)}
                          onChange={(e) =>
                            setValues((current) => {
                              const next = new Map(current);
                              next.set(
                                `${weekday}:${hour}`,
                                Math.max(1, Number(e.target.value) || 1),
                              );
                              return next;
                            })
                          }
                          className="h-9 w-14 rounded-md border border-line bg-surface text-center text-ink"
                        />
                        <input
                          type="hidden"
                          name="capacity"
                          value={`${weekday}:${hour}:${capacityAt(weekday, hour)}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          type="submit"
          disabled={pending}
          className={`${buttonClass("secondary")} mt-4`}
        >
          {pending ? "Saving…" : "Save capacity"}
        </button>
      </form>
    </section>
  );
}
